import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getEmployees, createLeave } from "services/hrms.api";
import { ChevronLeft } from "lucide-react";
import { useAuth } from "views/auth/AuthContext"; // adjust path if different

export default function NewLeavePage() {
  const nav = useNavigate();
  const { user } = useAuth(); // expects user = { id, role, name, employeeCode? }
  const isAdminLike = ["admin", "superadmin", "owner", "manager"].includes(
    (user?.role || "").toLowerCase()
  );

  const [emps, setEmps] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const today = new Date().toISOString().slice(0, 10);

  // form state
  const [form, setForm] = useState(() => ({
    employeeName: "",
    employeeCode: "", // admin/manager flow uses this
    type: "CL",
    from: today,
    to: today,
    days: 1,
    reason: "",
  }));

  const firstInputRef = useRef(null);

  // Load employees if admin/manager (for search list)
  useEffect(() => {
    (async () => {
      if (!isAdminLike) return;
      try {
        const { data } = await getEmployees();
        const items = Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data)
          ? data
          : [];
        setEmps(
          items.map((e) => ({
            id: e.id || e._id || e.code,
            code: e.code || e.id || e._id,
            name: e.name || "",
          }))
        );
      } catch (e) {
        console.error(e);
      }
    })();
  }, [isAdminLike]);

  // Auto-calc days (inclusive)
  useEffect(() => {
    const a = new Date(form.from);
    const b = new Date(form.to || form.from);
    if (!isNaN(a) && !isNaN(b)) {
      const ms = Math.max(0, +b - +a);
      const days = Math.floor(ms / 86400000) + 1;
      setForm((p) => ({ ...p, days }));
    }
  }, [form.from, form.to]);

  // Autofill current employee (non-admin)
  useEffect(() => {
    if (!isAdminLike && (user?.name || user?.employeeCode)) {
      setForm((p) => ({
        ...p,
        employeeName: user?.name || p.employeeName,
        employeeCode: user?.employeeCode || p.employeeCode,
      }));
    }
  }, [isAdminLike, user?.name, user?.employeeCode]);

  const set = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  // Robust parser for "Name (CODE)" even if typed manually
  const parseNameAndCode = (val) => {
    const v = (val || "").trim();
    // Try regex: "... (CODE)"
    const m = v.match(/^(.*?)(?:\s*\(\s*([^)]+)\s*\))\s*$/);
    if (m) {
      const name = m[1].trim();
      const code = m[2].trim();
      return { name, code };
    }
    // Try to resolve by exact name from emps
    const lower = v.toLowerCase();
    const byName = emps.find((e) => e.name.toLowerCase() === lower);
    if (byName) return { name: byName.name, code: byName.code };
    // Try to resolve by "Name (CODE)" that doesn't exactly match casing/spacing
    const combo = emps.find(
      (e) => `${e.name} (${e.code})`.toLowerCase() === lower
    );
    if (combo) return { name: combo.name, code: combo.code };
    // Fallback: keep name, clear code
    return { name: v, code: "" };
  };

  const onEmployeeInput = (val) => {
    const { name, code } = parseNameAndCode(val);
    setForm((p) => ({ ...p, employeeName: name, employeeCode: code }));
  };

  const canSubmit = useMemo(() => {
    if (!form.type || !form.from || !form.to) return false;
    if (isAdminLike) {
      // Admin must have a resolvable employeeCode
      return Boolean(form.employeeCode && form.employeeName);
    }
    return true; // employee self-service
  }, [
    form.type,
    form.from,
    form.to,
    form.employeeCode,
    form.employeeName,
    isAdminLike,
  ]);

  const submit = async (e) => {
    e.preventDefault();

    // Extra guard: if admin and employeeCode empty, try resolve once more
    if (isAdminLike && !form.employeeCode && form.employeeName) {
      const resolved = emps.find(
        (e) => e.name.toLowerCase() === form.employeeName.toLowerCase()
      );
      if (resolved) {
        setForm((p) => ({ ...p, employeeCode: resolved.code }));
      } else {
        setErr(
          "Please pick an employee from the list or include the code in parentheses e.g., Rahul (EMP-007)."
        );
        return;
      }
    }

    if (!canSubmit) {
      setErr("Please complete the form.");
      return;
    }

    setErr("");
    setSaving(true);
    try {
      const payload = {
        type: form.type,
        from: form.from,
        to: form.to,
        reason: form.reason?.trim() || undefined,
      };

      // Admin/manager must send code so backend can resolve employee
      if (isAdminLike) {
        payload.code = form.employeeCode;
        if (!payload.code) {
          throw new Error("Employee code is required for admin/manager.");
        }
      }
      // Employee users: backend uses req.user; do not send code/employee

      await createLeave(payload);
      nav("/admin/hrms/leaves");
    } catch (e1) {
      const msg =
        e1?.response?.data?.message ||
        e1?.message ||
        "Failed to create leave. (Possible overlap or validation error)";
      setErr(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto  space-y-6 p-4 md:p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => nav(-1)}
            className="flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50 dark:border-white/10 dark:hover:bg-navy-700 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:text-white"
          >
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
          <h2 className="text-2xl font-semibold dark:text-white">New Leave</h2>
        </div>
      </div>

      {err && (
        <div className="border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-200 rounded-lg border p-3 text-sm">
          {err}
        </div>
      )}

      <form onSubmit={submit} className="grid gap-4 md:grid-cols-2">
        {/* Employee picker (Admin/Manager only) */}
        {isAdminLike ? (
          <>
            <Field label="Employee">
              <input
                ref={firstInputRef}
                list="empList"
                value={`${form.employeeName}${
                  form.employeeCode ? ` (${form.employeeCode})` : ""
                }`}
                onChange={(e) => onEmployeeInput(e.target.value)}
                placeholder="Type name or 'Name (CODE)'…"
                className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
                required
              />
              <datalist id="empList">
                {emps.map((e) => (
                  <option key={e.code} value={`${e.name} (${e.code})`} />
                ))}
              </datalist>
            </Field>

            <Field label="Employee Code">
              <input
                name="employeeCode"
                value={form.employeeCode}
                onChange={set}
                placeholder="EMP-001"
                className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
                required
              />
            </Field>
          </>
        ) : (
          // Employee user - just show who they are
          <div className="md:col-span-2">
            <div className="rounded-lg border px-3 py-2 text-sm dark:border-white/10">
              <div className="text-xs text-gray-500">Employee</div>
              <div className="mt-0.5 font-medium">
                {form.employeeName || user?.name || "You"}
              </div>
            </div>
          </div>
        )}

        <Field label="Type">
          <select
            name="type"
            value={form.type}
            onChange={set}
            className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
          >
            <option>CL</option>
            <option>SL</option>
            <option>PL</option>
            <option>LWP</option>
          </select>
        </Field>

        <Field label="Days">
          <input
            type="number"
            min="1"
            name="days"
            value={form.days}
            readOnly
            className="w-full cursor-not-allowed rounded-lg border bg-gray-50 px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5 dark:text-white dark:bg-navy-800"
            title="Auto-calculated from date range"
          />
        </Field>

        <Field label="From">
          <input
            type="date"
            name="from"
            value={form.from}
            min={today}
            onChange={(e) => {
              const v = e.target.value;
              setForm((p) => ({ ...p, from: v, to: p.to < v ? v : p.to }));
            }}
            className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
            required
          />
        </Field>

        <Field label="To">
          <input
            type="date"
            name="to"
            value={form.to}
            min={form.from || today}
            onChange={set}
            className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
            required
          />
        </Field>

        <div className="md:col-span-2">
          <Field label="Reason">
            <textarea
              name="reason"
              rows={3}
              value={form.reason}
              onChange={set}
              placeholder="Why is the leave needed?"
              className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
            />
          </Field>
        </div>

        <div className="flex items-center justify-end gap-3 md:col-span-2">
          <button
            type="button"
            onClick={() => nav(-1)}
            className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:hover:bg-white/5 dark:text-white"
          >
            Cancel
          </button>
          <button
            disabled={saving || !canSubmit}
            className="rounded-lg bg-brand-500 px-5 py-2 text-sm text-white hover:opacity-95 disabled:opacity-60"
          >
            {saving ? "Submitting…" : "Submit"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-white/80">
        {label}
      </label>
      {children}
    </div>
  );
}
